import React from "react";
import {
  getBoosters,
  getReports,
  updateBooster as updateBoosterService,
  updateReport as updateReportService,
  warnUser as warnUserService,
  banUser as banUserService,
  getTransactionsPrints,
  getPrintSheet,
  updateTransactionShippingStatus,
  getShippingSlip,
  getBoosterPrintSheet,
  getRefundRequested,
  refundBooster,
  refundPrint,
  refuseBoosterRefund,
  refusePrintRefund,
} from "../../services/AdminServices";
import { ReportSchema } from "../../services/ReportServices";
import { ShowCardsType } from "../../services/CardServices";
import homeStyles from "../Home/Home.module.css";
import { reportStatusEnum } from "../../enums/reportStatusEnum";
import globalStyles from "../../styles/globalStyles.module.css";
import reportStyles from "../../components/Buttons/ReportButton/ReportButton.module.css";
import { useAuth } from "../../context/AuthProvider";
import {
  getInvoice,
  printTransactionData,
  StoreTransactionsData,
} from "../../services/TransactionServices";
import orderStyles from "../Orders/Orders.module.css";
import { transactionStatusEnum } from "../../enums/transactionStatusEnum";
import { useTranslation } from "react-i18next";
import { shippingStatusEnum } from "../../enums/shippingStatusEnum";

export const Admin = () => {
  const { user } = useAuth();

  if (user?.role !== "admin") {
    return <div>You do not have permission to access this page.</div>;
  }

  return (
    <div>
      <Reports />
      <hr style={{ margin: "2rem 0" }} />
      <RefundRequested />
      <hr style={{ margin: "2rem 0" }} />
      <Boosters />
      <hr style={{ margin: "2rem 0" }} />
      <Transactions />
      <hr style={{ margin: "2rem 0" }} />
      <h1>Users</h1>
      <WarnUser />
      <hr style={{ margin: "2rem 0" }} />
      <BanUser />
    </div>
  );
};

const RefundRequested = () => {
  const [transactions, setTransactions] = React.useState<
    (StoreTransactionsData & { type: string })[]
  >([]);
  const { t } = useTranslation();

  const fetchTransactions = async () => {
    try {
      const response = await getRefundRequested();
      setTransactions(response);
    } catch (error) {
      console.error("Failed to fetch boosters:", error);
    }
  };

  const handleRefund = async (
    transaction: StoreTransactionsData & { type: string }
  ) => {
    try {
      let response;
      if (transaction.type === "print") {
        response = await refundPrint(
          transaction.id,
          transaction.booster_id,
          transaction.comment
        );
      } else if (transaction.type === "digital") {
        response = await refundBooster(
          transaction.id,
          transaction.booster_id,
          transaction.comment
        );
      }

      console.log("Refund response:", response);
    } catch (error) {
      console.error("Failed to refund order:", error);
    }
  };

  const handleRefuseRefund = async (
    transaction: StoreTransactionsData & { type: string }
  ) => {
    try {
      if (transaction.type === "print") {
        await refusePrintRefund(
          transaction.id,
          transaction.booster_id,
          transactionStatusEnum.REFUND_DENIED,
          transaction.comment
        );
      } else {
        await refuseBoosterRefund(
          transaction.id,
          transaction.booster_id,
          transactionStatusEnum.REFUND_DENIED,
          transaction.comment
        );
      }
    } catch (error) {
      console.error("Error updating shipping status:", error);
    }
  };
  return (
    <>
      <h1>Refund Requests</h1>
      <button onClick={fetchTransactions}>Fetch Transactions</button>
      {transactions.map((transaction, index) => (
        <div key={index} className={orderStyles.item}>
          <img
            src={transaction.image_id}
            alt={transaction.name}
            className={orderStyles.boosterImage}
          />
          <p>{transaction.id}</p>
          <div>{transaction.quantity}</div>
          <div>{transaction.comment}</div>
          <div>{transaction.type}</div>
          <div>
            {transaction.status === transactionStatusEnum.REFUNDED ? (
              <span style={{ textDecoration: "line-through" }}>
                {transaction.price}€
              </span>
            ) : (
              `${transaction.price}€`
            )}
          </div>
          <button
            className={globalStyles.button}
            onClick={async () => handleRefund(transaction)}
          >
            {t("refund")}
          </button>
          <button
            className={globalStyles.button}
            onClick={async () => handleRefuseRefund(transaction)}
          >
            {t("refuse")}
          </button>
        </div>
      ))}
    </>
  );
};

const Transactions = () => {
  const [transactions, setTransactions] = React.useState<
    printTransactionData[]
  >([]);
  const { t } = useTranslation();

  const fetchTransactions = async () => {
    try {
      const response = await getTransactionsPrints();
      setTransactions(response);
    } catch (error) {
      console.error("Failed to fetch boosters:", error);
    }
  };

  const fetchPrintSheet = async (transactionId: number, boosterId: string) => {
    try {
      const url = await getPrintSheet(transactionId, boosterId);

      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = transactionId + ".html";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {
      console.error("Error fetching print sheet:", error);
    }
  };

  const fetchBoosterPrintSheet = async (
    transactionId: number,
    boosterId: string
  ) => {
    try {
      const url = await getBoosterPrintSheet(transactionId, boosterId);

      const anchor = document.createElement("a");
      anchor.href = url;
      console.log("Booster print sheet URL:", url);
      anchor.download = transactionId + ".pdf";
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    } catch (error) {
      console.error("Error fetching print sheet:", error);
    }
  };

  const handleGetInvoice = async (transactionId: number) => {
    try {
      const invoiceUrl = await getInvoice(transactionId);

      // Create a temporary <a> element
      const link = document.createElement("a");
      link.href = invoiceUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error fetching receipt URL:", error);
    }
  };

  const handleShippingStatusUpdate = async (
    transactionId: number,
    status: shippingStatusEnum
  ) => {
    try {
      // Call the API to update the shipping status
      // Assuming there's a function updateShippingStatus that takes transactionId and status
      await updateTransactionShippingStatus(transactionId, status);
      // Optionally, you can refetch transactions or update the state to reflect the change
    } catch (error) {
      console.error("Error updating shipping status:", error);
    }
  };

  const handleGetShippingSlip = async (transactionId: number) => {
    try {
      const slipBase64 = await getShippingSlip(transactionId);
      const byteCharacters = atob(slipBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Create a Blob URL and open or print
      const blobUrl = URL.createObjectURL(blob);

      // Option 1: Open in a new tab (view or print)
      window.open(blobUrl, "_blank");

      // Option 2: Trigger download
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "shipping-slip.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error fetching shipping slip:", error);
    }
  };

  return (
    <>
      <h1>Transactions</h1>
      <button onClick={fetchTransactions}>Fetch Transactions</button>
      {transactions.map((transaction, index) => (
        <div key={index} className={orderStyles.item}>
          <img
            src={transaction.image_id}
            alt={transaction.name}
            className={orderStyles.boosterImage}
          />
          <p>{transaction.id}</p>
          <div>{transaction.quantity}</div>
          <div>
            {transaction.status === transactionStatusEnum.REFUNDED ? (
              <span style={{ textDecoration: "line-through" }}>
                {transaction.price}€
              </span>
            ) : (
              `${transaction.price}€`
            )}
          </div>
          <div>{transaction.shipping_status}</div>
          <button
            className={globalStyles.button}
            onClick={async () => handleGetInvoice(transaction.id)}
          >
            {t("invoice.download")}
          </button>
          <button
            className={globalStyles.button}
            onClick={async () =>
              fetchBoosterPrintSheet(transaction.id, transaction.booster_id)
            }
          >
            {t("booster sheet")}
          </button>
          <button
            className={globalStyles.button}
            onClick={async () =>
              fetchPrintSheet(transaction.id, transaction.booster_id)
            }
          >
            {t("card sheet")}
          </button>
          <button
            onClick={() => {
              handleShippingStatusUpdate(
                transaction.id,
                shippingStatusEnum.PACKING
              );
            }}
            className={globalStyles.button}
          >
            PRINTED
          </button>
          <button
            onClick={() => {
              handleGetShippingSlip(transaction.id);
            }}
            className={globalStyles.button}
          >
            Slip
          </button>
          <button
            onClick={() => {
              handleShippingStatusUpdate(
                transaction.id,
                shippingStatusEnum.SHIPPED
              );
            }}
            className={globalStyles.button}
          >
            SHIPPED
          </button>
        </div>
      ))}
      <h4>Get invoice</h4>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.target as HTMLFormElement;
          const id = (form.querySelector("#transactionId") as HTMLInputElement)
            .value;
          handleGetInvoice(Number(id));
        }}
        className={reportStyles.formContainer}
      >
        <input
          type="text"
          id="transactionId"
          className={globalStyles.input}
          placeholder="Invoice ref"
        />
        <button type="submit" className={globalStyles.button}>
          getInvoice
        </button>
      </form>
    </>
  );
};

const Boosters = () => {
  const [boosters, setBoosters] = React.useState<ShowCardsType[]>([]);
  const [comments, setComments] = React.useState<Record<string, string>>({});
  const fetchBoosters = async () => {
    try {
      const response = await getBoosters();
      setBoosters(response);
    } catch (error) {
      console.error("Failed to fetch boosters:", error);
    }
  };

  const groupedBoosters = boosters.reduce<
    Record<
      string,
      { booster_name: string; small_image_id: string; booster_image: string }[]
    >
  >((acc, booster) => {
    if (!acc[booster.booster_id!]) {
      acc[booster.booster_id!] = [];
    }
    acc[booster.booster_id!].push({
      booster_name: booster.booster_name,
      small_image_id: booster.small_image_id,
      booster_image: booster.booster_image,
    });
    return acc;
  }, {});

  const updateBooster = async (
    boosterId: string,
    e?: React.FormEvent,
    statusOverride?: string
  ) => {
    if (e) e.preventDefault();

    const status = statusOverride;
    if (!status) return; // Prevent invalid call

    if (!status) return; // Prevent invalid call
    try {
      await updateBoosterService(
        boosterId,
        status as reportStatusEnum,
        comments[boosterId]
      );
      setComments((prev) => ({ ...prev, [boosterId]: "" }));
      setBoosters((prev) =>
        prev.filter((booster) => booster.booster_id !== boosterId)
      );
    } catch (error) {
      console.error("Failed to update booster:", error);
    }
  };

  return (
    <>
      <h1>Boosters</h1>
      <button onClick={fetchBoosters}>Fetch booster</button>

      {boosters.length > 0 ? (
        <ul>
          {Object.entries(groupedBoosters).map(([booster_id, items], index) => (
            <div className="d-flex flex-column" key={booster_id}>
              <li key={index} className="d-flex">
                <img
                  src={items[0].booster_image}
                  alt={`Booster ${index}`}
                  className={homeStyles.boosterImage}
                />
                <div>
                  {items.map((item, idx) => (
                    <img
                      key={idx}
                      src={item.small_image_id}
                      className={homeStyles.cardImage}
                      alt={`Small ${idx}`}
                    />
                  ))}
                </div>
              </li>
              <form className={reportStyles.formContainer}>
                <label htmlFor={`Comment-${booster_id}`}>Comment</label>
                <input
                  id={`Comment-${booster_id}`}
                  value={comments[booster_id] || ""}
                  onChange={(e) =>
                    setComments((prev) => ({
                      ...prev,
                      [booster_id]: e.target.value,
                    }))
                  }
                  className={globalStyles.input}
                />
                <label htmlFor={`status-${booster_id}`}>status:</label>

                <div className={"d-flex"}>
                  <button
                    type="button"
                    className={globalStyles.button}
                    style={{ backgroundColor: "green" }}
                    onClick={() => {
                      updateBooster(
                        booster_id,
                        undefined,
                        reportStatusEnum.approved
                      );
                    }}
                  >
                    Approve
                  </button>

                  <button
                    type="button"
                    className={globalStyles.button}
                    style={{ backgroundColor: "red" }}
                    onClick={() => {
                      updateBooster(
                        booster_id,
                        undefined,
                        reportStatusEnum.rejected
                      );
                    }}
                  >
                    Reject
                  </button>
                </div>
              </form>
            </div>
          ))}
        </ul>
      ) : (
        <p>No boosters.</p>
      )}
    </>
  );
};

const Reports = () => {
  const [reports, setReports] = React.useState<ReportSchema[]>([]);
  const [comments, setComments] = React.useState<Record<string, string>>({});

  const fetchReports = async () => {
    try {
      const response = await getReports();
      setReports(response);
    } catch (error) {
      console.error("Failed to fetch reports:", error);
    }
  };

  const updateBooster = async (boosterId: string, status?: string) => {
    try {
      await updateBoosterService(
        boosterId,
        status as reportStatusEnum,
        comments[boosterId]
      );
    } catch (error) {
      console.error("Failed to update booster:", error);
    }
  };

  const warmUser = async (userId: string, reason: string, comment?: string) => {
    try {
      await warnUserService(userId, reason, comment);
    } catch (error) {
      console.error("Failed to warn user:", error);
    }
  };

  const updateReport = async (
    reportId: number,
    e?: React.FormEvent,
    statusOverride?: string
  ) => {
    if (e) e.preventDefault();

    const status = statusOverride;
    if (!status) return; // Prevent invalid call

    try {
      await updateReportService(
        reportId,
        status as reportStatusEnum,
        comments[reportId]
      );

      const foundReport = reports.find((r) => r.id === reportId);
      const page = foundReport?.page;
      if (
        page &&
        page.includes("/boosters/") &&
        status === reportStatusEnum.rejected
      ) {
        updateBooster(page.split("/").pop()!, status);
      }

      if (
        page &&
        page.includes("/profile/") &&
        status === reportStatusEnum.rejected
      ) {
        warmUser(page.split("/").pop()!, foundReport.reason, "warn from user");
      }

      setComments((prev) => ({ ...prev, [reportId]: "" }));
      // setReports((prev) => prev.filter((report) => report.id !== reportId));
    } catch (error) {
      console.error("Failed to update report:", error);
    }
  };
  return (
    <>
      <h1>Reports</h1>
      <button onClick={fetchReports}>Fetch report</button>
      {reports.length > 0 ? (
        <ul>
          {reports.map((report, index) => (
            <li key={index}>
              <a href={report.page} target="_blank" rel="noopener noreferrer">
                {report.page}
              </a>{" "}
              <div>
                <strong>Reason: </strong> {report.reason}
              </div>
              <div>
                <strong>description: </strong>
                {report.description}
              </div>
              <form className={reportStyles.formContainer}>
                <label htmlFor={`Comment-${report.id}`}>Comment</label>
                <input
                  id={`Comment-${report.id}`}
                  value={comments[report.id] || ""}
                  onChange={(e) =>
                    setComments((prev) => ({
                      ...prev,
                      [report.id]: e.target.value,
                    }))
                  }
                  className={globalStyles.input}
                />
                <div className={"d-flex"}>
                  <button
                    type="button"
                    className={globalStyles.button}
                    style={{ backgroundColor: "green" }}
                    onClick={() => {
                      updateReport(
                        report.id,
                        undefined,
                        reportStatusEnum.approved
                      );
                    }}
                  >
                    Ignore report
                  </button>

                  <button
                    type="button"
                    className={globalStyles.button}
                    style={{ backgroundColor: "red" }}
                    onClick={() => {
                      updateReport(
                        report.id,
                        undefined,
                        reportStatusEnum.rejected
                      );
                    }}
                  >
                    Ban item
                  </button>
                </div>
              </form>
            </li>
          ))}
        </ul>
      ) : (
        <p>No reports.</p>
      )}
    </>
  );
};

const WarnUser = () => {
  const warmUser = async (
    username: string,
    reason: string,
    comment?: string
  ) => {
    try {
      // Assuming warnUser is a function that sends a warning to the user
      await warnUserService(username, reason, comment);
      alert("User warned successfully");
    } catch (error) {
      console.error("Failed to warn user:", error);
      alert("Failed to warn user");
    }
  };

  return (
    <>
      <h4>Warn User</h4>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.target as HTMLFormElement;
          const username = (form.querySelector("#username") as HTMLInputElement)
            .value;
          const reason = (form.querySelector("#reason") as HTMLInputElement)
            .value;
          const comment = (form.querySelector("#comment") as HTMLInputElement)
            .value;

          warmUser(username, reason, comment);
        }}
        className={reportStyles.formContainer}
      >
        <label htmlFor="username">User ID:</label>
        <input
          type="text"
          id="username"
          className={globalStyles.input}
          placeholder="Enter username"
        />
        <label htmlFor="reason">Reason:</label>
        <input
          type="text"
          id="reason"
          className={globalStyles.input}
          placeholder="Enter reason for warning"
        />
        <label htmlFor="comment">Comment:</label>
        <input
          type="text"
          id="comment"
          className={globalStyles.input}
          placeholder="Enter comment (optional)"
        />
        <button type="submit" className={globalStyles.button}>
          Warn User
        </button>
      </form>
    </>
  );
};

const BanUser = () => {
  const banUser = async (
    username: string,
    reason: string,
    comment?: string
  ) => {
    try {
      // Assuming warnUser is a function that sends a warning to the user
      await banUserService(username, reason, comment);
      alert("User banned successfully");
    } catch (error) {
      console.error("Failed to warn user:", error);
      alert("Failed to ban user");
    }
  };

  return (
    <>
      <h4>Ban User</h4>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const form = e.target as HTMLFormElement;
          const username = (form.querySelector("#username") as HTMLInputElement)
            .value;
          const reason = (form.querySelector("#reason") as HTMLInputElement)
            .value;
          const comment = (form.querySelector("#comment") as HTMLInputElement)
            .value;

          banUser(username, reason, comment);
        }}
        className={reportStyles.formContainer}
      >
        <label htmlFor="username">User ID:</label>
        <input
          type="text"
          id="username"
          className={globalStyles.input}
          placeholder="Enter user ID"
        />
        <label htmlFor="reason">Reason:</label>
        <input
          type="text"
          id="reason"
          className={globalStyles.input}
          placeholder="Enter reason for warning"
        />
        <label htmlFor="comment">Comment:</label>
        <input
          type="text"
          id="comment"
          className={globalStyles.input}
          placeholder="Enter comment (optional)"
        />
        <button type="submit" className={globalStyles.button}>
          Ban User
        </button>
      </form>
    </>
  );
};
